/* Brandlabs theme script. Vanilla web components, no dependencies.

   window.Theme is the theme's public API for apps and custom code (ES modules import the same helpers from 'theme'):
   Theme.Element (base class), Theme.define, Theme.wait, Theme.fine / reducedMotion / isDesktop / isTablet (media
   checks), Theme.belongsTo, Theme.loading (top loading bar), Theme.formatMoney, Theme.lockScroll / unlockScroll,
   Theme.joinSections, and from sections.js Theme.QuickBuy, Theme.Lightbox and Theme.stagger. */

const Theme = (window.Theme = window.Theme || {});
const mq = (q) => window.matchMedia(q);
export const fine = () => mq('(hover: hover) and (pointer: fine)').matches;
export const reducedMotion = () => mq('(prefers-reduced-motion: reduce)').matches;
export const isDesktop = () => mq('(min-width: 1000px)').matches;
export const isTablet = () => mq('(min-width: 700px)').matches;
const define = (name, cls) => customElements.get(name) || customElements.define(name, cls);
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

Object.assign(Theme, { fine, reducedMotion, isDesktop, isTablet, define, wait });

/* Base for the theme's web components. connectedCallback fires again every time an element is moved
   (filter drawer to <body>, quick buy, re-rendered parents), so one-time wiring lives in setup(), which
   runs once per element. connected() / disconnected() run on every insertion and removal, for window
   listeners and observers that must be released. */
export class ThemeElement extends HTMLElement {
  connectedCallback() {
    if (!this._isSetUp) {
      this._isSetUp = true;
      this.setup?.();
    }
    this.connected?.();
  }

  disconnectedCallback() {
    this.disconnected?.();
  }
}
Theme.Element = ThemeElement;

// Drawers, modals and phone popovers move to <body> while open (see XDialog.show), so a node inside one
// still belongs to the element its dialog came from.
export const belongsTo = (root, node) => {
  if (!node || !root.isConnected) return false;
  if (root.contains(node)) return true;
  const dialog = node.closest?.('x-dialog');
  return Boolean(dialog?.homeNode && dialog.homeNode !== dialog && root.contains(dialog.homeNode));
};
Theme.belongsTo = belongsTo;

// Fetches a section rendering (recommendations, recently viewed, cart upsells) and returns the element matching
// `selector` from it, or null when the request fails or the element is missing.
export const fetchSectionElement = async (url, selector) => {
  try {
    const response = await fetch(url, { priority: 'low' });
    if (!response.ok) return null;
    return new DOMParser().parseFromString(await response.text(), 'text/html').querySelector(selector);
  } catch {
    return null;
  }
};

// Runs non-urgent work once the browser is idle; Safari has no requestIdleCallback, so it waits a short moment.
export const whenIdle = (callback) => ('requestIdleCallback' in window ? requestIdleCallback(callback) : setTimeout(callback, 200));

// Pointer vs keyboard input, for focus rings (see .is-pointer-input in theme.css).
document.addEventListener('pointerdown', () => document.documentElement.classList.add('is-pointer-input'), true);
document.addEventListener('keydown', (event) => {
  if (event.key === 'Tab' || event.key.startsWith('Arrow')) document.documentElement.classList.remove('is-pointer-input');
}, true);

/* ---------------------------------------------------------------------------
   Live regions and the top loading bar
--------------------------------------------------------------------------- */
Theme.announce = (message, assertive = false) => {
  const region = document.getElementById(assertive ? 'a11y-alert' : 'a11y-status');
  if (!region) return;
  region.textContent = '';
  setTimeout(() => (region.textContent = message), 100);
};

// One sweep per burst of requests: requests that follow each other closely (a cart change, then the
// section refresh) keep the bar running instead of restarting it.
// end() resolves once the bar starts finishing (or a new request takes over), so callers can wait
// for it before showing the result.
let loadingCount = 0;
let loadingEndTimer = null;
let loadingEndResolve = null;
const settleLoadingEnd = () => {
  loadingEndResolve?.();
  loadingEndResolve = null;
};
Theme.loading = {
  start() {
    const bar = document.querySelector('.loading-bar');
    loadingCount++;
    clearTimeout(loadingEndTimer);
    settleLoadingEnd();
    if (!bar || bar.classList.contains('is-loading')) return;
    bar.classList.remove('is-done');
    void bar.offsetWidth;
    bar.classList.add('is-loading');
  },
  // `immediate` finishes the bar now instead of waiting to see whether another request follows.
  end({ immediate = false } = {}) {
    loadingCount = Math.max(0, loadingCount - 1);
    if (loadingCount) return Promise.resolve();
    clearTimeout(loadingEndTimer);
    settleLoadingEnd();
    return new Promise((resolve) => {
      loadingEndResolve = resolve;
      loadingEndTimer = setTimeout(() => {
        const bar = document.querySelector('.loading-bar');
        if (bar && !loadingCount) {
          bar.classList.add('is-done');
          bar.classList.remove('is-loading');
          setTimeout(() => !loadingCount && bar.classList.remove('is-done'), 500);
        }
        settleLoadingEnd();
      }, immediate ? 0 : 120);
    });
  },
};

/* Fetch wrapper that drives the loading bar */
Theme.fetch = async (url, options = {}) => {
  Theme.loading.start();
  try {
    return await fetch(url, options);
  } finally {
    Theme.loading.end();
  }
};

Theme.fetchSection = async (url, sectionId, options) => {
  const target = new URL(url, location.origin);
  target.searchParams.set('section_id', sectionId);
  const response = await Theme.fetch(target, options);
  const html = await response.text();
  return new DOMParser().parseFromString(html, 'text/html');
};

/* ---------------------------------------------------------------------------
   Scroll lock (reference counted)
--------------------------------------------------------------------------- */
const locks = new Set();
Theme.lockScroll = (owner) => {
  locks.add(owner);
  document.documentElement.classList.add('is-locked');
};
Theme.unlockScroll = (owner) => {
  locks.delete(owner);
  if (!locks.size) document.documentElement.classList.remove('is-locked');
};

/* ---------------------------------------------------------------------------
   Focus trap
--------------------------------------------------------------------------- */
const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), summary, iframe';

const visibleFocusables = (root) =>
  [...root.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el.getClientRects().length);

class FocusTrap {
  constructor(root) {
    this.root = root;
    this.onKey = this.onKey.bind(this);
  }
  activate(initial) {
    document.addEventListener('keydown', this.onKey);
    if (!initial) return;
    initial.focus({ preventScroll: true });
    // A panel whose visibility is still transitioning in cannot take focus yet: try again once it shows.
    if (document.activeElement !== initial) {
      requestAnimationFrame(() => initial.focus({ preventScroll: true }));
      setTimeout(() => document.activeElement !== initial && this.root.contains(initial) && initial.focus({ preventScroll: true }), 60);
    }
  }
  deactivate() {
    document.removeEventListener('keydown', this.onKey);
  }
  onKey(event) {
    if (event.key !== 'Tab') return;
    const items = visibleFocusables(this.root);
    if (!items.length) return event.preventDefault();
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || !this.root.contains(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !this.root.contains(document.activeElement))) {
      event.preventDefault();
      first.focus();
    }
  }
}

/* ---------------------------------------------------------------------------
   <x-dialog type="drawer|modal|popover" id="..."> — shared overlay behaviour.
   Openers: any element with aria-controls="<id>".
--------------------------------------------------------------------------- */
const openDialogs = [];

class XDialog extends ThemeElement {
  static get observedAttributes() {
    return ['open'];
  }

  setup() {
    this.type = this.getAttribute('type') || 'modal';
    this.classList.add(`is-${this.type}`);
    this.panel = this.querySelector(':scope > .overlay-panel') || this.querySelector('.overlay-panel');
    this.trap = new FocusTrap(this.panel || this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    if (this.panel) {
      this.panel.setAttribute('role', 'dialog');
      if (this.type !== 'popover') this.panel.setAttribute('aria-modal', 'true');
      this.panel.setAttribute('tabindex', '-1');
    }
    // Delegated, so close buttons and backdrops rendered later (cart refreshes, quick buy) keep working.
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-dialog-close], .overlay-backdrop');
      if (trigger && trigger.closest('x-dialog') === this) this.hide();
    });
  }

  disconnected() {
    if (this.hasAttribute('open') && !this._moving) this.cleanup();
  }

  // Where the dialog lives in the page, also while it is moved to <body> (a placeholder marks the spot).
  get homeNode() {
    return this._home || this;
  }

  get openers() {
    return document.querySelectorAll(`[aria-controls="${this.id}"]`);
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  /* Popovers behave as bottom sheets below 1000px */
  get isSheet() {
    return this.type !== 'popover' || !isDesktop();
  }

  toggle(opener) {
    this.isOpen ? this.hide() : this.show(opener);
  }

  async show(opener) {
    if (this.isOpen) return;
    clearTimeout(this._closeTimer);
    this.classList.remove('is-closing');
    this.opener = opener || document.activeElement;
    this.dispatchEvent(new CustomEvent('dialog:before-show', { bubbles: true }));

    if (this.isSheet && !this.hasAttribute('no-reparent') && this.parentElement !== document.body) {
      this._home = document.createComment('dialog-home');
      this._moving = true;
      this.before(this._home);
      document.body.append(this);
      this._moving = false;
      void this.offsetWidth;
    }

    // A dialog opened from inside another (quick buy from the cart drawer) stacks above it.
    this.style.setProperty('--dialog-layer', openDialogs.length);
    this.setAttribute('open', '');
    this.openers.forEach((el) => el.setAttribute('aria-expanded', 'true'));
    openDialogs.push(this);
    if (this.type !== 'popover' || !isDesktop()) Theme.lockScroll(this);

    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('pointerdown', this.onPointerDown);
    document.addEventListener('pointerup', this.onPointerUp);

    // Only the search panel puts the caret in its field (pointer devices only); other dialogs focus their panel.
    let initial = this.panel;
    if (this.type === 'search' && fine()) initial = this.panel?.querySelector('input[type="search"], input[name="q"]') || this.panel;
    if (!this.hasAttribute('no-trap')) this.trap.activate(initial);
    else initial?.focus({ preventScroll: true });

    this.dispatchEvent(new CustomEvent('dialog:show', { bubbles: true }));
    await wait(this.type === 'popover' && isDesktop() ? 200 : 300);
    if (this.isOpen) this.dispatchEvent(new CustomEvent('dialog:after-show', { bubbles: true }));
  }

  hide() {
    if (!this.isOpen) return;
    this.removeAttribute('open');
    this.classList.add('is-closing');
    this.openers.forEach((el) => el.setAttribute('aria-expanded', 'false'));
    this.dispatchEvent(new CustomEvent('dialog:hide', { bubbles: true }));
    const duration = this.type === 'drawer' || (this.isSheet && !isTablet()) ? 300 : 200;
    this._closeTimer = setTimeout(() => {
      this.classList.remove('is-closing');
      this.cleanup();
      if (this.opener && document.contains(this.opener)) this.opener.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('dialog:after-hide', { bubbles: true }));
    }, duration);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('pointerdown', this.onPointerDown);
    document.removeEventListener('pointerup', this.onPointerUp);
    this.trap.deactivate();
    const index = openDialogs.indexOf(this);
    if (index > -1) openDialogs.splice(index, 1);
  }

  cleanup() {
    Theme.unlockScroll(this);
    if (this._home) {
      // Its section may have been re-rendered while it was open (sorting); the old copy then goes away.
      this._moving = true;
      if (this._home.isConnected) this._home.replaceWith(this);
      else this.remove();
      this._moving = false;
      this._home = null;
    }
  }

  onKeyDown(event) {
    // An inner control that already handled Escape (e.g. the cart note panel) prevents its default.
    if (event.key === 'Escape' && !event.defaultPrevented && openDialogs[openDialogs.length - 1] === this) {
      event.preventDefault();
      this.hide();
    }
  }

  onPointerDown(event) {
    this._downInside = this.panel?.contains(event.target);
  }

  /* Taps are judged where the finger lifts */
  onPointerUp(event) {
    if (openDialogs[openDialogs.length - 1] !== this) return;
    const target = document.elementFromPoint(event.clientX, event.clientY) || event.target;
    if (this.panel?.contains(target) || this._downInside) return;
    if ([...this.openers].some((o) => o.contains(target))) return;
    if (target.closest('x-dialog') && target.closest('x-dialog') !== this) return;
    this.hide();
  }

  attributeChangedCallback() {}
}
define('x-dialog', XDialog);

/* Openers: aria-controls pointing at an x-dialog toggles it */
document.addEventListener('click', (event) => {
  const opener = event.target.closest('[aria-controls]');
  if (!opener) return;
  const dialog = document.getElementById(opener.getAttribute('aria-controls'));
  if (!(dialog instanceof XDialog)) return;
  event.preventDefault();
  dialog.toggle(opener);
});

/* ---------------------------------------------------------------------------
   Accordions: animated <details class="accordion">
--------------------------------------------------------------------------- */
class XAccordion extends ThemeElement {
  setup() {
    this.details = this.querySelector('details');
    this.summary = this.details?.querySelector('summary');
    this.content = this.summary?.nextElementSibling;
    if (!this.summary) return;
    this.summary.addEventListener('click', (event) => this.onClick(event));
  }

  onClick(event) {
    const link = this.summary.getAttribute('data-href');
    if (link && this.details.open) return (location.href = link);
    if (reducedMotion()) return;
    event.preventDefault();
    const closing = this.details.classList.contains('is-closing');
    this.details.open && !closing ? this.close() : this.open();
  }

  // Opening grows the row over 250 ms and fades the content in after 150 ms;
  // closing starts everything on the tap (icon, content fade over 150 ms, row shrinking over 250 ms).
  async open() {
    const d = this.details;
    const start = d.offsetHeight;
    d.getAnimations().forEach((a) => a.cancel());
    d.classList.remove('is-closing');
    d.open = true;
    const end = d.offsetHeight;
    this.content.getAnimations().forEach((a) => a.cancel());
    this.content.animate(
      [{ opacity: 0, transform: 'translateY(4px)' }, { opacity: 1, transform: 'none' }],
      { duration: 150, delay: 150, easing: 'ease', fill: 'backwards' }
    );
    d.style.overflow = 'clip';
    await d.animate({ height: [`${start}px`, `${end}px`] }, { duration: 250, easing: 'ease' }).finished;
    d.style.overflow = '';
  }

  async close() {
    const d = this.details;
    const start = d.offsetHeight;
    const end = this.summary.offsetHeight + parseFloat(getComputedStyle(d).borderTopWidth) + parseFloat(getComputedStyle(d).borderBottomWidth);
    d.classList.add('is-closing');
    d.style.overflow = 'clip';
    this.content.animate({ opacity: [1, 0] }, { duration: 150, easing: 'ease', fill: 'forwards' });
    await d.animate({ height: [`${start}px`, `${end}px`] }, { duration: 250, easing: 'ease' }).finished;
    if (!d.classList.contains('is-closing')) return;
    d.open = false;
    d.classList.remove('is-closing');
    d.style.overflow = '';
    this.content.getAnimations().forEach((a) => a.cancel());
  }
}
define('x-accordion', XAccordion);

/* ---------------------------------------------------------------------------
   <x-carousel> — native scroll-snap carousel.
   Children with [data-cell] are the slides. Controls point at it with
   aria-controls and data-carousel="prev|next|dot".
--------------------------------------------------------------------------- */
class XCarousel extends ThemeElement {
  setup() {
    this.index = 0;
    this.onScroll = this.onScroll.bind(this);
    this.addEventListener('scroll', this.onScroll, { passive: true });
    if ('onscrollend' in window) {
      this.addEventListener('scrollend', () => this.settle());
    } else {
      // No scrollend (older Safari): settle once scrolling has been quiet for a moment and no finger is down.
      let timer;
      let touching = false;
      const queue = () => {
        clearTimeout(timer);
        timer = setTimeout(() => !touching && this.settle(), 120);
      };
      this.addEventListener('touchstart', () => (touching = true), { passive: true });
      this.addEventListener('touchend', () => ((touching = false), queue()), { passive: true });
      this.addEventListener('scroll', queue, { passive: true });
    }
    this.controls = () => document.querySelectorAll(`[aria-controls="${this.id}"][data-carousel]`);
    document.addEventListener('click', (event) => {
      const control = event.target.closest(`[aria-controls="${this.id}"][data-carousel]`);
      if (!control) return;
      const action = control.getAttribute('data-carousel');
      if (action === 'prev') this.prev();
      else if (action === 'next') this.next();
      else if (action === 'dot') {
        const mediaId = control.getAttribute('data-media-id');
        const byMedia = mediaId ? this.cells.findIndex((c) => c.getAttribute('data-media-id') === mediaId) : -1;
        this.select(byMedia > -1 ? byMedia : Number(control.getAttribute('data-index')));
      }
    });
    if (this.hasAttribute('draggable-scroll')) this.enableDrag();
    requestAnimationFrame(() => this.update());
    new ResizeObserver(() => this.update()).observe(this);
    if (this.hasAttribute('autoplay')) this.startAutoplay();
  }

  get cells() {
    return [...this.querySelectorAll(this.getAttribute('cell-selector') || ':scope > [data-cell]')].filter((c) => !c.hidden);
  }

  get isRtl() {
    return getComputedStyle(this).direction === 'rtl';
  }

  cellOffset(cell) {
    const align = getComputedStyle(cell).scrollSnapAlign.split(' ').pop();
    const pad = parseFloat(getComputedStyle(this).scrollPaddingInlineStart) || 0;
    const rel = cell.getBoundingClientRect().left - this.getBoundingClientRect().left + this.scrollLeft;
    if (align === 'center') return rel - (this.clientWidth - cell.clientWidth) / 2;
    return rel - pad;
  }

  select(index, behavior = reducedMotion() ? 'auto' : 'smooth') {
    const cells = this.cells;
    if (!cells.length) return;
    index = Math.max(0, Math.min(index, cells.length - 1));
    this.scrollTo({ left: this.cellOffset(cells[index]), behavior });
    this.setIndex(index);
  }

  /* Pages move one viewport of cells at a time */
  /* Cells per page from the distance between two cells, not a cell's own width: a cell can be narrower than its
     slot (quick buy images, or an image still loading), which would otherwise count as two per page. */
  perPage() {
    const cells = this.cells;
    const step = cells.length > 1 ? Math.abs(cells[1].offsetLeft - cells[0].offsetLeft) : 0;
    return Math.max(1, Math.round(this.clientWidth / (step || this.clientWidth)));
  }

  next() {
    const cells = this.cells;
    const perPage = this.perPage();
    if (this.hasAttribute('loop') && (this.atEnd() || this.index === cells.length - 1)) return this.wrap('tail', 0);
    this.select(Math.min(this.index + (this.hasAttribute('page-by-cell') ? 1 : perPage), cells.length - 1));
  }

  prev() {
    const cells = this.cells;
    const perPage = this.perPage();
    if (this.hasAttribute('loop') && this.index === 0) return this.wrap('head', cells.length - 1);
    this.select(Math.max(this.index - (this.hasAttribute('page-by-cell') ? 1 : perPage), 0));
  }

  /* Past either end: onto a <gallery-loop> clone when there is one (it swaps in the real cell once the scroll rests),
     otherwise straight back to the other end. */
  wrap(position, index) {
    const clone = this.querySelector(`:scope > [data-clone="${position}"]`);
    if (!clone) return this.select(index);
    this.scrollTo({ left: this.cellOffset(clone), behavior: reducedMotion() ? 'auto' : 'smooth' });
  }

  atEnd() {
    return Math.abs(this.scrollLeft) + this.clientWidth >= this.scrollWidth - 2;
  }

  onScroll() {
    if (this._frame) return;
    this._frame = requestAnimationFrame(() => {
      this._frame = null;
      this.update();
    });
  }

  nearestIndex() {
    const box = this.getBoundingClientRect();
    let best = 0;
    let dist = Infinity;
    this.cells.forEach((cell, i) => {
      const r = cell.getBoundingClientRect();
      const align = getComputedStyle(cell).scrollSnapAlign.split(' ').pop();
      const pad = parseFloat(getComputedStyle(this).scrollPaddingInlineStart) || 0;
      const d = align === 'center' ? Math.abs(r.left + r.width / 2 - (box.left + box.width / 2)) : Math.abs(r.left - box.left - pad);
      if (d < dist) {
        dist = d;
        best = i;
      }
    });
    return best;
  }

  update() {
    const index = this.atEnd() && this.scrollWidth > this.clientWidth + 2 ? this.cells.length - 1 : this.nearestIndex();
    if (index !== this.index) this.setIndex(index);
    this.updateControls();
  }

  setIndex(index) {
    const changed = index !== this.index;
    this.index = index;
    this.cells.forEach((cell, i) => cell.classList.toggle('is-selected', i === index));
    this.updateControls();
    if (changed) this.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index, cell: this.cells[index] } }));
  }

  updateControls() {
    const scrollable = this.scrollWidth > this.clientWidth + 2;
    const loop = this.hasAttribute('loop');
    this.controls().forEach((control) => {
      const action = control.getAttribute('data-carousel');
      if (action === 'prev') control.disabled = !loop && (!scrollable || Math.abs(this.scrollLeft) < 2);
      else if (action === 'next') control.disabled = !loop && (!scrollable || this.atEnd());
      else if (action === 'dot' && !control.hasAttribute('data-media-id')) control.setAttribute('aria-current', String(Number(control.getAttribute('data-index')) === this.index));
    });
  }

  settle() {
    this.update();
    this.dispatchEvent(new CustomEvent('carousel:settle', { bubbles: true, detail: { index: this.index } }));
  }

  startAutoplay() {
    const delay = Number(this.getAttribute('autoplay')) * 1000 || 5000;
    const tick = () => {
      if (!this.matches(':hover') && !document.hidden) this.atEnd() ? this.select(0) : this.next();
    };
    this._autoplay = setInterval(tick, delay);
  }

  enableDrag() {
    let startX = 0;
    let startScroll = 0;
    let dragging = false;
    this.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse') return;
      dragging = true;
      startX = event.clientX;
      startScroll = this.scrollLeft;
      this.classList.add('is-dragging');
    });
    window.addEventListener('pointermove', (event) => {
      if (!dragging) return;
      this.scrollLeft = startScroll - (event.clientX - startX);
      if (Math.abs(event.clientX - startX) > 5) this._dragged = true;
    });
    window.addEventListener('pointerup', () => {
      if (!dragging) return;
      dragging = false;
      this.classList.remove('is-dragging');
      this.select(this.nearestIndex());
      setTimeout(() => (this._dragged = false));
    });
    this.addEventListener('click', (event) => this._dragged && event.preventDefault(), true);
  }
}
define('x-carousel', XCarousel);

/* ---------------------------------------------------------------------------
   Reveal on scroll: [data-reveal-group] reveals its [data-reveal] children
   with a staggered fade-and-rise. Settings decide whether it runs.
--------------------------------------------------------------------------- */
const easeOutStagger = (i, count, step) => {
  if (count <= 1) return 0;
  const total = step * (count - 1);
  const t = i / (count - 1);
  return total * (1 - Math.pow(1 - t, 2)) ;
};

class RevealGroup extends ThemeElement {
  connected() {
    if (!document.documentElement.classList.contains('js-reveal') || reducedMotion()) {
      this.items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const margin = this.getAttribute('margin') || '0px';
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        this.observer.disconnect();
        this.reveal();
      },
      { rootMargin: `0px 0px ${margin} 0px` }
    );
    // A display: contents group has no box of its own, so watch its items instead.
    const targets = getComputedStyle(this).display === 'contents' ? this.items : [this];
    (targets.length ? targets : [this]).forEach((el) => this.observer.observe(el));
  }

  disconnected() {
    this.observer?.disconnect();
  }

  get items() {
    return [...this.querySelectorAll('[data-reveal]')];
  }

  reveal() {
    const items = this.items.filter((el) => !el.classList.contains('is-revealed'));
    const type = this.getAttribute('type') || 'products';
    const config = {
      products: { rise: 20, duration: 200, step: 50, delay: 400, easing: 'ease-in-out' },
      posts: { rise: 30, duration: 250, step: 100, delay: 0, easing: 'ease' },
    }[type];
    items.forEach((el, i) => {
      const delay = config.delay + easeOutStagger(i, items.length, config.step);
      el.animate(
        [{ opacity: 0, transform: `translateY(${config.rise}px)` }, { opacity: 1, transform: 'none' }],
        { duration: config.duration, delay, easing: config.easing, fill: 'backwards' }
      );
      el.classList.add('is-revealed');
    });
  }

  replay() {
    this.items.forEach((el) => el.classList.remove('is-revealed'));
    this.reveal();
  }
}
define('reveal-group', RevealGroup);

/* ---------------------------------------------------------------------------
   Tabs
--------------------------------------------------------------------------- */
class XTabs extends ThemeElement {
  // Panel switch: the old panel fades out dropping 10px, the container eases to the new height, then the
  // new panel fades in rising 10px. `in-delay` (ms after the height starts) and `easing` let a section tune it.
  setup() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.wrapper = this.querySelector('.tabs__panels');
    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (event) => {
        const dir = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (!dir) return;
        event.preventDefault();
        const next = (i + dir + this.tabs.length) % this.tabs.length;
        this.tabs[next].focus();
        this.select(next);
      });
    });
    if (window.Shopify?.designMode) {
      // Selecting a block in the theme editor shows its tab.
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.tabs.findIndex((tab) => tab.dataset.blockId && tab.dataset.blockId === event.detail?.blockId);
        if (index > -1) this.select(index);
      });
    }
  }

  async select(index) {
    const current = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    if (index === current || this.busy) return;
    this.tabs.forEach((t, i) => {
      t.setAttribute('aria-selected', String(i === index));
      t.tabIndex = i === index ? 0 : -1;
    });
    const from = this.panels[current];
    const to = this.panels[index];
    if (reducedMotion() || !from || !this.wrapper.offsetParent) {
      this.panels.forEach((p) => (p.hidden = p !== to));
      return;
    }
    const easing = this.getAttribute('easing') || 'ease';
    const delay = Number(this.getAttribute('in-delay') || 100);
    this.busy = true;
    // Each step waits for its animation, but never longer than its duration: a background tab may never finish one.
    const done = (animation, ms) => Promise.race([animation.finished, wait(ms + 50)]).catch(() => {});
    const startHeight = this.wrapper.offsetHeight;
    await done(from.animate([{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'translateY(10px)' }], { duration: 300, easing, fill: 'forwards' }), 300);
    from.hidden = true;
    to.hidden = false;
    from.getAnimations().forEach((a) => a.cancel());
    const endHeight = this.wrapper.offsetHeight;
    this.wrapper.style.overflow = 'clip';
    to.animate([{ opacity: 0, transform: 'translateY(10px)' }, { opacity: 1, transform: 'none' }], { duration: 300, delay, easing, fill: 'backwards' });
    await done(this.wrapper.animate({ height: [`${startHeight}px`, `${endHeight}px`] }, { duration: 150, easing: 'cubic-bezier(0.85, 0, 0.15, 1)' }), 150);
    this.wrapper.style.overflow = '';
    await wait(Math.max(0, delay + 300 - 150));
    this.busy = false;
  }
}
define('x-tabs', XTabs);

/* ---------------------------------------------------------------------------
   Quantity selector
--------------------------------------------------------------------------- */
class QuantitySelector extends ThemeElement {
  setup() {
    this.input = this.querySelector('input');
    this.querySelectorAll('button[data-step]').forEach((button) =>
      button.addEventListener('click', () => {
        const step = Number(this.input.step) || 1;
        const min = Number(this.input.min) || 0;
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const value = Number(this.input.value) + step * Number(button.getAttribute('data-step'));
        this.input.value = Math.min(max, Math.max(min, value));
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      })
    );
    this.input.addEventListener('input', () => this.sync());
    this.input.addEventListener('change', () => this.sync());
    this.sync();
  }

  sync() {
    this.style.setProperty('--digits', String(this.input.value).length);
    const min = Number(this.input.min) || 0;
    const max = this.input.max ? Number(this.input.max) : Infinity;
    const minus = this.querySelector('[data-step="-1"]');
    const plus = this.querySelector('[data-step="1"]');
    if (minus) minus.disabled = Number(this.input.value) <= min;
    if (plus) plus.disabled = Number(this.input.value) >= max;
  }
}
define('quantity-selector', QuantitySelector);

/* ---------------------------------------------------------------------------
   Sticky offsets for anchors
--------------------------------------------------------------------------- */
Theme.updateStickyOffset = () => {
  const root = document.documentElement.style;
  // Schema classes, present from the first byte (the header and bar scripts add their own classes later).
  const header = document.querySelector('.bl-section--header');
  const bar = document.querySelector('.bl-section--announcement-bar');
  // Fractional heights: a rounded value leaves a sub-pixel gap under the header.
  const headerHeight = header ? header.getBoundingClientRect().height : 0;
  const barHeight = bar ? bar.getBoundingClientRect().height : 0;
  root.setProperty('--header-height', `${headerHeight}px`);
  root.setProperty('--announcement-height', `${barHeight}px`);
  let offset = 0;
  if (bar?.hasAttribute('data-sticky')) offset += barHeight;
  const headerShown = header?.hasAttribute('data-sticky') && !header.classList.contains('is-hidden');
  if (headerShown) offset += headerHeight;
  root.setProperty('--sticky-offset', `${offset}px`);
  // Sticky bars below a bordered header overlap its bottom line, so the two borders read as one.
  root.setProperty('--sticky-border-overlap', headerShown && header.querySelector('.header--bordered') ? '1px' : '0px');
};

/* In-page anchors scroll smoothly below the sticky area, without a history entry */
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[href*="#"]');
  if (!link || link.hasAttribute('data-allow-hash') || link.classList.contains('skip-link')) return;
  const url = new URL(link.href, location.href);
  if (url.pathname !== location.pathname || url.search !== location.search || !url.hash || url.hash === '#') return;
  // #product-reviews also finds a review app's widget when the page has no Reviews section.
  const reviewApps = '#looxReviews, .jdgm-review-widget, .yotpo-main-widget, #shopify-product-reviews';
  const target = document.getElementById(decodeURIComponent(url.hash.slice(1))) || (url.hash === '#product-reviews' ? document.querySelector(reviewApps) : null);
  if (!target) return;
  event.preventDefault();
  // Lands below the sticky header through the root's scroll-padding-top.
  target.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  target.dispatchEvent(new CustomEvent('anchor:target', { bubbles: true }));
  document.dispatchEvent(new CustomEvent('theme:hash', { detail: { hash: url.hash } }));
});

/* ---------------------------------------------------------------------------
   Theme editor support
--------------------------------------------------------------------------- */
if (window.Shopify?.designMode) {
  // The layout sets the transparent header classes on <body> when the page loads; keep them right while the
  // merchant edits, reorders or removes the first section.
  const syncTransparentHeader = () => {
    const request = document.querySelector('#main > .shopify-section:first-child [data-transparent-header]');
    document.body.classList.toggle('has-transparent-header', Boolean(request));
    document.body.classList.toggle('has-transparent-header--primary', request?.dataset.transparentHeader === 'primary');
    Theme.updateStickyOffset();
  };

  // The editor fires block:select again after every setting change (detail.load). Only picking a block opens its
  // drawer; a re-render keeps the drawer as it was, so a drawer the merchant closed stays closed.
  const editorOpenDialogs = new Set();
  document.addEventListener('dialog:show', (event) => event.target.id && editorOpenDialogs.add(event.target.id));
  document.addEventListener('dialog:hide', (event) => event.target.id && editorOpenDialogs.delete(event.target.id));
  document.addEventListener('shopify:block:select', (event) => {
    const dialog = event.target.closest('x-dialog');
    if (dialog && (!event.detail?.load || editorOpenDialogs.has(dialog.id))) dialog.show();
    event.target.closest('x-carousel')?.select([...event.target.parentElement.children].indexOf(event.target), 'auto');
  });
  document.addEventListener('shopify:block:deselect', (event) => event.target.closest('x-dialog')?.hide());
  document.addEventListener('shopify:section:select', (event) => {
    event.target.querySelector('[data-open-on-select]')?.show?.();
  });
  document.addEventListener('shopify:section:deselect', (event) => event.target.querySelector('[data-open-on-select]')?.hide?.());
  // An open drawer or modal lives in <body> (see XDialog.show). When the editor re-renders its section, the new
  // copy lands in the section while the old one would stay open in <body>, still showing the old settings. Put
  // the old one away as its section unloads, and open the new copy in its place.
  const reopenAfterLoad = new Set();
  document.addEventListener('shopify:section:unload', (event) => {
    document.querySelectorAll('body > x-dialog[open]').forEach((dialog) => {
      if (!dialog._home || !event.target.contains(dialog._home)) return;
      reopenAfterLoad.add(dialog.id);
      dialog.hide();
      clearTimeout(dialog._closeTimer);
      dialog.classList.remove('is-closing');
      dialog.cleanup();
    });
    setTimeout(() => {
      Theme.joinSections();
      syncTransparentHeader();
    });
  });
  document.addEventListener('shopify:section:load', (event) => {
    Theme.joinSections();
    syncTransparentHeader();
    reopenAfterLoad.forEach((id) => {
      const dialog = event.target.querySelector(`x-dialog#${CSS.escape(id)}`);
      reopenAfterLoad.delete(id);
      dialog?.show();
    });
  });
  document.addEventListener('shopify:section:reorder', () => {
    Theme.joinSections();
    syncTransparentHeader();
  });
}

/* ---------------------------------------------------------------------------
   Currency rate for amounts entered in the store's currency (snippets/amount-cents.liquid). Liquid has no exchange
   rates, so the live rate Shopify gives the page is kept in a hidden cart attribute; when it changes, the cart is
   updated and the drawer re-rendered with the converted amounts.
--------------------------------------------------------------------------- */
Theme.syncCurrencyRate = async () => {
  const { currency } = window.Shopify || {};
  const settings = window.themeSettings || {};
  if (!currency?.active || currency.active === settings.shopCurrency) return;
  const value = `${currency.active}:${currency.rate}`;
  if (settings.currencyRate === value) return;
  settings.currencyRate = value;
  await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ attributes: { _currency_rate: value } }),
  });
  // The drawer and announcement bar re-render; sections.js defines Theme.Cart, which may not have loaded yet.
  if (Theme.Cart) Theme.Cart.refresh();
  else window.addEventListener('load', () => Theme.Cart?.refresh(), { once: true });
};
Theme.syncCurrencyRate();

/* ---------------------------------------------------------------------------
   Money
--------------------------------------------------------------------------- */
Theme.formatMoney = (cents, format = window.themeSettings?.moneyFormat || '${{amount}}') => {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = Number(cents);
  const fmt = (n, decimals, thousands = ',', decimal = '.') => {
    const fixed = (n / 100).toFixed(decimals);
    const [whole, frac] = fixed.split('.');
    return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  const hideZero = window.themeSettings?.hideZeroCents && value % 100 === 0;
  return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    if (hideZero) key = key.replace('amount_with', 'amount_no_decimals_with').replace(/^amount$/, 'amount_no_decimals');
    switch (key) {
      case 'amount': return fmt(value, 2);
      case 'amount_no_decimals': return fmt(value, 0);
      case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
      case 'amount_with_space_separator': return fmt(value, 2, ' ', ',');
      case 'amount_no_decimals_with_space_separator': return fmt(value, 0, ' ', ',');
      case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
      case 'amount_no_decimals_with_apostrophe_separator': return fmt(value, 0, "'", '.');
      default: return fmt(value, 2);
    }
  });
};

/* ---------------------------------------------------------------------------
   Expected delivery (snippets/delivery-promise.liquid). Works in the warehouse's time
   zone: shipping and delivery skip non-working days and the listed holidays. With a
   countdown, the "before" wording shows until today's cut-off on a shipping day.
--------------------------------------------------------------------------- */
const DAY_SETS = { mon_fri: [1, 2, 3, 4, 5], mon_sat: [1, 2, 3, 4, 5, 6], every_day: [0, 1, 2, 3, 4, 5, 6] };
const DAY_MS = 86400000;

class DeliveryPromise extends ThemeElement {
  connected() {
    this.update();
  }

  disconnected() {
    clearTimeout(this.timer);
  }

  /* Calendar parts of "now" in the given time zone, as a UTC midnight timestamp plus seconds since midnight. */
  zonedNow(timeZone) {
    const options = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' };
    let parts;
    try {
      parts = new Intl.DateTimeFormat('en-US', { ...options, timeZone }).formatToParts(new Date());
    } catch (error) {
      parts = new Intl.DateTimeFormat('en-US', options).formatToParts(new Date()); // unknown time zone: the shopper's own
    }
    const get = (type) => Number(parts.find((part) => part.type === type)?.value || 0);
    return { day: Date.UTC(get('year'), get('month') - 1, get('day')), seconds: get('hour') * 3600 + get('minute') * 60 + get('second') };
  }

  update() {
    clearTimeout(this.timer);
    const data = this.dataset;
    const shipDays = DAY_SETS[data.shipDays] || DAY_SETS.mon_fri;
    const deliverDays = DAY_SETS[data.deliverDays] || DAY_SETS.mon_fri;
    const holidays = new Set((data.holidays || '').split(',').filter(Boolean));
    const iso = (day) => new Date(day).toISOString().slice(0, 10);
    const works = (day, days) => days.includes(new Date(day).getUTCDay()) && !holidays.has(iso(day));
    const next = (day, days) => {
      let d = day + DAY_MS;
      for (let i = 0; i < 400 && !works(d, days); i++) d += DAY_MS;
      return d;
    };
    const advance = (day, count, days) => {
      let d = day;
      for (let i = 0; i < count; i++) d = next(d, days);
      return d;
    };

    const [hours, minutes] = (data.cutoff || '16:00').split(':').map((value) => Number(value) || 0);
    const cutoff = hours * 3600 + minutes * 60;
    const now = this.zonedNow(data.timeZone);
    const beforeCutoff = works(now.day, shipDays) && now.seconds < cutoff;
    let shipDay = beforeCutoff ? now.day : next(now.day, shipDays);
    shipDay = advance(shipDay, Number(data.processing) || 0, shipDays);
    const minDay = advance(shipDay, Number(data.min) || 0, deliverDays);
    const maxDay = advance(shipDay, Math.max(Number(data.max) || 0, Number(data.min) || 0), deliverDays);

    const locale = data.locale || document.documentElement.lang || 'en';
    const dateOptions = {
      weekday_day_month: { weekday: 'short', day: 'numeric', month: 'short' },
      weekday_day_month_long: { weekday: 'long', day: 'numeric', month: 'long' },
      day_month_long: { day: 'numeric', month: 'long' },
      day_month: { day: 'numeric', month: 'short' },
      weekday: { weekday: 'long' }
    }[data.dateFormat] || { weekday: 'short', day: 'numeric', month: 'short' };
    const dateFormat = new Intl.DateTimeFormat(locale, { ...dateOptions, timeZone: 'UTC' });
    // One date by default: the latest expected day, so the promise is never too early. A range is optional.
    const range = data.dateMode !== 'range' || minDay === maxDay ? dateFormat.format(maxDay) : dateFormat.formatRange ? dateFormat.formatRange(minDay, maxDay) : `${dateFormat.format(minDay)} – ${dateFormat.format(maxDay)}`;
    const relative = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const date = data.tomorrow === 'true' && data.dateMode !== 'range' && maxDay - now.day === DAY_MS ? relative.format(1, 'day') : range;
    const dayOffset = Math.round((shipDay - now.day) / DAY_MS);
    const shipText = dayOffset <= 1 ? relative.format(dayOffset, 'day') : new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(shipDay);

    // After the cut-off the countdown either hides until the next day or, with "keep counting", runs to the next one.
    const countingDown = beforeCutoff || data.countdownAlways === 'true';
    const nextCutoff = beforeCutoff ? cutoff : (next(now.day, shipDays) - now.day) / 1000 + cutoff;
    const left = Math.max(0, nextCutoff - now.seconds);
    const pad = (value) => String(value).padStart(2, '0');
    const h = Math.floor(left / 3600);
    const m = Math.floor((left % 3600) / 60);
    const shownMinutes = h > 0 ? m : Math.max(1, m);
    const unit = (value, name) => new Intl.NumberFormat(locale, { style: 'unit', unit: name, unitDisplay: 'long' }).format(value);
    const countdown = {
      words: () => (h > 0 ? data.hours : data.minutes).replace('[hours]', h).replace('[minutes]', shownMinutes),
      words_long: () => new Intl.ListFormat(locale, { type: 'conjunction' }).format(h > 0 ? [unit(h, 'hour'), ...(m > 0 ? [unit(m, 'minute')] : [])] : [unit(shownMinutes, 'minute')]),
      clock_minutes: () => `${pad(h)}:${pad(shownMinutes)}`
    }[data.countdownFormat]?.() ?? `${pad(h)}:${pad(m)}:${pad(left % 60)}`;

    const values = { countdown, date, date_min: dateFormat.format(minDay), date_max: dateFormat.format(maxDay), ship_day: shipText };
    this.querySelectorAll('[data-slot]').forEach((slot) => { slot.textContent = values[slot.dataset.slot] ?? ''; });
    // Without a countdown there is only the "after" wording, shown all day.
    const before = this.querySelector('[data-before]');
    if (before) {
      before.hidden = !countingDown;
      this.querySelector('[data-after]').hidden = countingDown;
    }
    this.classList.add('is-ready');

    // The full clock ticks every second; the other formats only change every minute. Either way, re-check right at the cut-off.
    const step = countingDown ? (data.countdownFormat === 'clock' || !data.countdownFormat ? 1 : Math.min(60, left)) : 60;
    this.timer = setTimeout(() => this.update(), Math.max(1, step) * 1000);
  }
}
define('delivery-promise', DeliveryPromise);

/* ---------------------------------------------------------------------------
   Rich text clean-up: pasted text often carries empty paragraphs and quotes
   (<p> </p>, <p>&nbsp;</p>). Truly empty ones are hidden by CSS; this hides the
   whitespace-only ones CSS cannot see. Elements holding media or a line break stay.
--------------------------------------------------------------------------- */
Theme.tidyProse = (root = document) => {
  root.querySelectorAll('.prose :is(p, blockquote, h1, h2, h3, h4, h5, h6)').forEach((el) => {
    if (el.textContent.trim() === '' && !el.querySelector('img, svg, video, iframe, input, br')) el.hidden = true;
  });
};

/* ---------------------------------------------------------------------------
   Boot
--------------------------------------------------------------------------- */
Theme.tidyProse();
document.addEventListener('shopify:section:load', (event) => Theme.tidyProse(event.target));
Theme.updateStickyOffset();
// The header and bar themselves are watched too: their size can settle (fonts, logo, bar messages) without changing the body's.
const stickyObserver = new ResizeObserver(() => Theme.updateStickyOffset());
[document.body, document.querySelector('.bl-section--header'), document.querySelector('.bl-section--announcement-bar')].forEach((el) => el && stickyObserver.observe(el));
